import React from "react";
import Cookies from "js-cookie";
import axios from "axios";
import { Helmet } from "react-helmet";
import styled from "styled-components";
import { formatUserDateTime } from "utils/timezoneHelper";

const PageContainer = styled.div`
  margin-top: 20px;
  padding: 10px;
  color: #f8fafc;
`;

const HeaderRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
`;

const Title = styled.h2`
  font-size: 20px;
  font-weight: 700;
  margin: 0;
`;

const ClearButton = styled.button`
  background: rgba(168, 85, 247, 0.15);
  border: 1px solid rgba(168, 85, 247, 0.3);
  color: #c084fc;
  padding: 8px 16px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: rgba(168, 85, 247, 0.25);
    border-color: rgba(168, 85, 247, 0.5);
  }
`;

const NotificationList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const NotificationCard = styled.div`
  background: ${props => props.isRead ? "rgba(30, 41, 59, 0.2)" : "rgba(168, 85, 247, 0.08)"};
  backdrop-filter: blur(10px);
  border: 1px solid ${props => props.isRead ? "rgba(255, 255, 255, 0.05)" : "rgba(168, 85, 247, 0.2)"};
  border-radius: 12px;
  padding: 16px;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  transition: all 0.2s;

  &:hover {
    border-color: rgba(168, 85, 247, 0.3);
  }
`;

const ContentCol = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const MsgTitle = styled.h4`
  font-size: 14px;
  font-weight: 700;
  margin: 0;
  color: ${props => props.isRead ? "#cbd5e1" : "#f8fafc"};
`;

const MsgBody = styled.p`
  font-size: 13px;
  color: #94a3b8;
  margin: 0;
`;

const TimeText = styled.span`
  font-size: 10px;
  color: #64748b;
  margin-top: 4px;
`;

const DeleteButton = styled.button`
  background: transparent;
  border: none;
  color: #64748b;
  cursor: pointer;
  padding: 4px;
  font-size: 16px;
  transition: color 0.2s;

  &:hover {
    color: #ef4444;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 40px 20px;
  color: #64748b;
  font-size: 14px;
  border: 1px dashed rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  background: rgba(30, 41, 59, 0.15);
`;

export default class Notifications extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      notifications: [],
      loading: true,
      uid: Cookies.get("uid") ? parseFloat(Cookies.get("uid")) : null
    };
  }

  componentDidMount() {
    document.body.scrollTop = 0;
    document.documentElement.scrollTop = 0;
    this.fetchNotifications();
  }

  fetchNotifications = async () => {
    const { uid } = this.state;
    if (!uid) {
      this.setState({ loading: false });
      return;
    }
    try {
      const res = await axios.get(`/api/campaigns/notifications/${uid}`);
      if (res.data && res.data.success) {
        this.setState({ notifications: res.data.notifications || [], loading: false });
      }
    } catch (err) {
      console.error("Error fetching notifications:", err);
      this.setState({ loading: false });
    }
  };

  markAllRead = async () => {
    const { uid } = this.state;
    if (!uid) return;
    try {
      await axios.post(`/api/campaigns/notifications/${uid}/read`);
      // Update local state to show all as read
      const updated = this.state.notifications.map(n => ({ ...n, read: true }));
      this.setState({ notifications: updated });
    } catch (err) {
      console.error("Error marking all read:", err);
    }
  };

  deleteNotif = async (id) => {
    try {
      await axios.delete(`/api/campaigns/notifications/${id}`);
      const filtered = this.state.notifications.filter(n => n.id !== id);
      this.setState({ notifications: filtered });
    } catch (err) {
      console.error("Error deleting notification:", err);
    }
  };

  render() {
    const { notifications, loading, uid } = this.state;

    if (!uid) {
      return (
        <PageContainer>
          <EmptyState>Please log in to view your notifications.</EmptyState>
        </PageContainer>
      );
    }

    return (
      <>
        <Helmet>
          <title>Notifications - Account Alerts</title>
          <meta name="description" content="View your rewards, system status, and campaign notifications." />
        </Helmet>
        <PageContainer>
          <HeaderRow>
            <Title>Notifications</Title>
            {notifications.length > 0 && (
              <ClearButton onClick={this.markAllRead}>Mark All as Read</ClearButton>
            )}
          </HeaderRow>

          {loading ? (
            <div style={{ textAlign: "center", padding: "20px", color: "#64748b" }}>Loading alerts...</div>
          ) : notifications.length === 0 ? (
            <EmptyState>You have no notifications at this time.</EmptyState>
          ) : (
            <NotificationList>
              {notifications.map((n) => (
                <NotificationCard key={n.id} isRead={n.read}>
                  <ContentCol>
                    <MsgTitle isRead={n.read}>{n.title}</MsgTitle>
                    <MsgBody>{n.message}</MsgBody>
                    <TimeText>{formatUserDateTime(n.created_at)}</TimeText>
                  </ContentCol>
                  <DeleteButton onClick={() => this.deleteNotif(n.id)}>&times;</DeleteButton>
                </NotificationCard>
              ))}
            </NotificationList>
          )}
        </PageContainer>
      </>
    );
  }
}
